/**
 * Обучение — курсы, уроки, прогресс (§9.1 ТЗ).
 *
 * Экран устроен вокруг одного правила: **порядок строгий**. Урок N+1
 * открывается после того, как сдан урок N, и закрытый урок не просто
 * серый — рядом написано, почему он закрыт. Замок без объяснения человек
 * читает как поломку и идёт к управляющему.
 *
 * Гейтинг при этом решает **сервер** (пункт приёмки 17): приложение
 * показывает то, что он прислал, и не вычисляет доступность само. Иначе
 * два места знали бы правило прохождения, и однажды они разошлись бы.
 *
 * Статус аттестации приходит готовой строкой. Формулировки «не допущен» в
 * ней не бывает — это D58: сервис показывает, что пройдено, а решение о
 * допуске к работе принимает человек и вне сервиса.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { LessonRow } from '../api';
import { Test } from './Test';

function statusText(row: LessonRow): string {
  if (row.is_locked) return row.locked_text ?? 'закрыт';
  if (row.status === 'passed') return 'сдан';
  if (row.status === 'read') return row.has_test ? 'прочитан, остался тест' : 'прочитан';
  return 'открыт';
}

function LessonScreen({ lessonId, onBack }: { lessonId: number; onBack: () => void }) {
  const queryClient = useQueryClient();
  const [testOpen, setTestOpen] = useState(false);
  const lesson = useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: () => studyApi.lesson(lessonId),
  });
  const read = useMutation({
    mutationFn: () => studyApi.markRead(lessonId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['course-plan'] });
      void queryClient.invalidateQueries({ queryKey: ['courses'] });
      onBack();
    },
  });

  if (lesson.isPending) return <Loading />;
  if (lesson.error || !lesson.data)
    return <ErrorState error={lesson.error} onRetry={() => lesson.refetch()} />;

  if (testOpen) {
    return (
      <Test
        lessonId={lessonId}
        onDone={() => {
          setTestOpen(false);
          // Сданный тест сдаёт урок — список должен это показать сразу.
          void queryClient.invalidateQueries({ queryKey: ['course-plan'] });
          void queryClient.invalidateQueries({ queryKey: ['courses'] });
          onBack();
        }}
      />
    );
  }

  return (
    <Card
      title={lesson.data.title}
      actions={
        <Button small variant="quiet" onClick={onBack}>
          К списку
        </Button>
      }
    >
      {/* Полоса над текстом урока (D91). Она есть и в самом тексте —
          урок читают с экрана, но и пересылают; в пересланном от нашей
          вёрстки не остаётся ничего. */}
      {lesson.data.demo_notice && <p className="demo-notice">{lesson.data.demo_notice}</p>}

      {/* Текст урока построчный: разметку добавим вместе с редактором
          (S5), а склеенные абзацы читать невозможно уже сейчас. */}
      <div className="lesson-body">{lesson.data.body}</div>

      {lesson.data.video_url && (
        <p>
          <a href={lesson.data.video_url} target="_blank" rel="noreferrer">
            Смотреть видео
          </a>
        </p>
      )}

      {lesson.data.has_test ? (
        <Button variant="primary" onClick={() => setTestOpen(true)}>
          Пройти тест
        </Button>
      ) : (
        <Button variant="primary" disabled={read.isPending} onClick={() => read.mutate()}>
          Дочитал — урок сдан
        </Button>
      )}
    </Card>
  );
}

function CourseScreen({ courseId, onBack }: { courseId: number; onBack: () => void }) {
  const [openLesson, setOpenLesson] = useState<number | null>(null);
  const plan = useQuery({
    queryKey: ['course-plan', courseId],
    queryFn: () => studyApi.coursePlan(courseId),
  });

  if (openLesson !== null) {
    return <LessonScreen lessonId={openLesson} onBack={() => setOpenLesson(null)} />;
  }

  if (plan.isPending) return <Loading />;
  if (plan.error || !plan.data)
    return <ErrorState error={plan.error} onRetry={() => plan.refetch()} />;

  return (
    <Card
      title="Уроки курса"
      actions={
        <Button small variant="quiet" onClick={onBack}>
          К курсам
        </Button>
      }
    >
      {plan.data.demo_notice && <p className="demo-notice">{plan.data.demo_notice}</p>}

      {plan.data.lessons.map((row) => (
        <div key={row.id} className="dish-line">
          <button
            type="button"
            className="dish-line__name"
            disabled={row.is_locked}
            onClick={() => setOpenLesson(row.id)}
          >
            {row.title}
          </button>
          <div className="dish-line__meta">
            <span className="faint">{statusText(row)}</span>
          </div>
        </div>
      ))}
    </Card>
  );
}

/**
 * Необязательные `courseId` и `onOpenCourse` — открытый курс снаружи: у
 * портала он в адресе (`/study/courses/7`). Без них — прежнее состояние
 * экрана, как в Mini App.
 */
export function Learning({
  courseId,
  onOpenCourse,
}: {
  courseId?: number | null;
  onOpenCourse?: (id: number | null) => void;
} = {}) {
  const [localCourse, setLocalCourse] = useState<number | null>(null);
  const openCourse = onOpenCourse ? (courseId ?? null) : localCourse;
  const setOpenCourse = onOpenCourse ?? setLocalCourse;
  const courses = useQuery({ queryKey: ['courses'], queryFn: studyApi.courses });
  // Тот же запрос, что в справочнике, и тот же ключ: react-query отдаст
  // его из кэша, второго обращения к серверу не будет.
  const demo = useQuery({
    queryKey: ['demo-state'],
    queryFn: studyApi.demoState,
    staleTime: Infinity,
  });

  if (openCourse !== null) {
    return <CourseScreen courseId={openCourse} onBack={() => setOpenCourse(null)} />;
  }

  if (courses.isPending) return <Loading />;
  if (courses.error || !courses.data)
    return <ErrorState error={courses.error} onRetry={() => courses.refetch()} />;

  if (courses.data.courses.length === 0) {
    return <Empty title="Курсов пока нет" text={courses.data.empty_text ?? undefined} />;
  }

  const demoBanner = demo.data?.courses ? demo.data.banner : null;

  return (
    <>
      {demoBanner && <p className="demo-banner">{demoBanner}</p>}

      <Card title="Обязательное обучение">
        <p>{courses.data.attestation}</p>
      </Card>

      <Card title="Мои курсы">
        {courses.data.courses.map((course) => (
          <div key={course.id} className="dish-line">
            <button
              type="button"
              className="dish-line__name"
              onClick={() => setOpenCourse(course.id)}
            >
              {course.demo_badge && <span className="demo-badge">{course.demo_badge}</span>}
              {course.title}
            </button>
            <div className="dish-line__meta">
              <span className="faint">
                {course.is_mandatory ? 'обязательный' : 'рекомендованный'} · сдано{' '}
                {course.passed} из {course.total}
                {course.is_complete && ' · пройден'}
              </span>
              {/* Срок словами сервера (D99): без «просрочено». */}
              {course.due_text && <span className="faint"> · {course.due_text}</span>}
            </div>
          </div>
        ))}
      </Card>
    </>
  );
}
